import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Plus, Search, Edit, Trash2, Eye, RefreshCw, Tag } from 'lucide-react';

interface PromoCode {
  id: string;
  company_id: string;
  code: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  description?: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  max_uses?: number;
  current_uses?: number;
  discount_duration_billing_cycles?: number;
  plan_codes?: string[];
  applies_to_billing?: string[];
  created_at: string;
  usage_count?: number;
}

async function fetchPromoCodes(params: { company_id?: string; is_active?: string; search?: string }) {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const queryParams = new URLSearchParams();
  if (params.company_id) queryParams.set('company_id', params.company_id);
  if (params.is_active !== undefined) queryParams.set('is_active', params.is_active);
  if (params.search) queryParams.set('search', params.search);

  const res = await fetch(`/api/admin/promo-codes?${queryParams.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch promo codes');
  const json = await res.json();
  return json.data as PromoCode[];
}

async function createPromoCode(data: Partial<PromoCode>) {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const res = await fetch('/api/admin/promo-codes', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create promo code');
  return res.json();
}

async function updatePromoCode(id: string, data: Partial<PromoCode>) {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`/api/admin/promo-codes/${id}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update promo code');
  return res.json();
}

async function deletePromoCode(id: string) {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`/api/admin/promo-codes/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to delete promo code');
  return res.json();
}

export default function PromoCodesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const { data } = await supabase.from('companies').select('id, name, brand_key').order('name');
      return data || [];
    },
  });

  const { data: promoCodes, isLoading, refetch } = useQuery({
    queryKey: ['promo-codes', companyId, activeFilter, search],
    queryFn: () => fetchPromoCodes({ company_id: companyId || undefined, is_active: activeFilter || undefined, search }),
    staleTime: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: deletePromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
    },
  });

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString();
  const formatDiscount = (code: PromoCode) =>
    code.discount_type === 'percentage' ? `${code.discount_value}%` : `$${(code.discount_value / 100).toFixed(2)}`;

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="h-6 w-6" />
            Promo Code Management
          </h1>
          <p className="text-sm text-gray-600">Create and manage discount codes with duration tracking</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            New Promo Code
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Input
          placeholder="Search codes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-48"
        />
        <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-48">
          <option value="">All Companies</option>
          {(companies || []).map((c: any) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} className="w-32">
          <option value="">All</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uses</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(promoCodes || []).map((code) => {
                const company = (companies || []).find((c: any) => c.id === code.company_id);
                return (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-bold text-primary-600">{code.code}</td>
                    <td className="px-4 py-3 text-sm">{company?.name || code.company_id}</td>
                    <td className="px-4 py-3 text-sm font-medium">{formatDiscount(code)}</td>
                    <td className="px-4 py-3 text-sm">
                      {code.discount_duration_billing_cycles
                        ? `${code.discount_duration_billing_cycles} billing cycles`
                        : <span className="text-gray-400">Forever</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(code.valid_from)} - {formatDate(code.valid_until)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${code.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {code.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{code.current_uses || 0} / {code.max_uses || '∞'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setEditingCode(code)} className="p-1 text-gray-500 hover:text-primary-600"><Edit className="h-4 w-4" /></button>
                        <button
                          onClick={() => {
                            if (confirm(`Deactivate promo code ${code.code}?`)) {
                              deleteMutation.mutate(code.id);
                            }
                          }}
                          className="p-1 text-gray-500 hover:text-red-600"
                        ><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(!promoCodes || promoCodes.length === 0) && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No promo codes found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCode) && (
        <PromoCodeModal
          code={editingCode}
          companies={companies || []}
          onClose={() => { setShowCreateModal(false); setEditingCode(null); }}
          onSave={() => { queryClient.invalidateQueries({ queryKey: ['promo-codes'] }); setShowCreateModal(false); setEditingCode(null); }}
        />
      )}
    </div>
  );
}

function PromoCodeModal({
  code,
  companies,
  onClose,
  onSave,
}: {
  code: PromoCode | null;
  companies: any[];
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState({
    company_id: code?.company_id || '',
    code: code?.code || '',
    discount_type: code?.discount_type || 'percentage',
    discount_value: code?.discount_value?.toString() || '',
    description: code?.description || '',
    discount_duration_billing_cycles: code?.discount_duration_billing_cycles?.toString() || '',
    max_uses: code?.max_uses?.toString() || '',
    is_active: code?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...formData,
        discount_value: Number(formData.discount_value),
        discount_duration_billing_cycles: formData.discount_duration_billing_cycles ? Number(formData.discount_duration_billing_cycles) : null,
        max_uses: formData.max_uses ? Number(formData.max_uses) : null,
      };
      if (code) {
        await updatePromoCode(code.id, payload);
      } else {
        await createPromoCode(payload);
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">{code ? 'Edit Promo Code' : 'Create Promo Code'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Company</label>
            <Select value={formData.company_id} onChange={(e) => setFormData({ ...formData, company_id: e.target.value })} required>
              <option value="">Select company</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Code</label>
            <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="e.g. NEW26" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <Select value={formData.discount_type} onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as any })}>
                <option value="percentage">Percentage</option>
                <option value="fixed_amount">Fixed Amount (cents)</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Value</label>
              <Input type="number" value={formData.discount_value} onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })} placeholder={formData.discount_type === 'percentage' ? '25' : '500'} required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Duration (billing cycles)</label>
            <Input type="number" value={formData.discount_duration_billing_cycles} onChange={(e) => setFormData({ ...formData, discount_duration_billing_cycles: e.target.value })} placeholder="Leave empty for forever" />
            <p className="text-xs text-gray-500 mt-1">Number of billing cycles before discount expires (e.g. 12 for first year)</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Max Uses</label>
            <Input type="number" value={formData.max_uses} onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })} placeholder="Leave empty for unlimited" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Internal description" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
            <label htmlFor="is_active" className="text-sm">Active</label>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

